import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alterarStatusPerfilTrilhoEstaleiro,
  cadastrarPerfilTrilhoEstaleiro,
  editarPerfilTrilhoEstaleiro,
  excluirPerfilTrilhoEstaleiro,
  listarPerfisTrilhoEstaleiro,
} from "./perfilTrilhosEstaleiro.api";
import CriarPerfilTrilhoModal from "./popups/CriarPerfilTrilhoModal";
import EditarPerfilTrilhoModal from "./popups/EditarPerfilTrilhoModal";
import ExcluirPerfilTrilhoModal from "./popups/ExcluirPerfilTrilhoModal";
import AtivarPerfilTrilhoModal from "./popups/AtivarPerfilTrilhoModal";
import InativarPerfilTrilhoModal from "./popups/InativarPerfilTrilhoModal";
import { useInfiniteBaseList } from "../shared/useInfiniteBaseList";
import "./PerfilTrilhosBase.css";

const toUpperTrim = (value, max = 40) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, max);

const safeText = (value) => String(value ?? "").trim();
const normalizePerfilNumero = (value, max = 3) => String(value ?? "").replace(/\D/g, "").slice(0, max);
const perfilFromNumero = (numero) => `TR ${normalizePerfilNumero(numero)}`;
const numeroFromPerfil = (perfil) => {
  const clean = String(perfil ?? "").toUpperCase();
  const match = clean.match(/TR\s*([0-9]+)/);
  if (match?.[1]) return normalizePerfilNumero(match[1]);
  return normalizePerfilNumero(clean);
};

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`bep-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="bep-toggle-knob" />
    </button>
  );
}

export default function PerfilTrilhosBase() {
  const [feedback, setFeedback] = useState("");

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [rowSelecionada, setRowSelecionada] = useState(null);
  const tableScrollRef = useRef(null);

  const { rows, loading, loadingMore, hasMore, error, reload, loadMore } = useInfiniteBaseList({
    listFn: listarPerfisTrilhoEstaleiro,
    queryText: filtroTexto,
    statusFilter: filtroStatus,
    pageSize: 100,
  });

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((row) => Number(row?.ativo) === 1).length;
    return {
      total,
      ativos,
      inativos: total - ativos,
    };
  }, [rows]);

  const rowsOrdenadas = useMemo(
    () => [...rows].sort((a, b) => safeText(a?.perfil_trilho).localeCompare(safeText(b?.perfil_trilho), "pt-BR", { sensitivity: "base" })),
    [rows]
  );

  const handleTableScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 120) {
        loadMore();
      }
    },
    [loadMore]
  );

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 12) {
      loadMore();
    }
  }, [rows.length, loading, loadingMore, hasMore, loadMore]);

  async function criarPerfil(numero) {
    const perfil = toUpperTrim(perfilFromNumero(numero), 40);
    await cadastrarPerfilTrilhoEstaleiro(perfil);
    setFeedback("Perfil cadastrado com sucesso.");
    reload();
  }

  async function editarPerfil(numero) {
    if (!rowSelecionada?.id) throw new Error("Perfil selecionado invalido.");
    const perfil = toUpperTrim(perfilFromNumero(numero), 40);
    await editarPerfilTrilhoEstaleiro(rowSelecionada.id, perfil);
    setFeedback("Perfil atualizado com sucesso.");
    reload();
  }

  async function excluirPerfil() {
    if (!rowSelecionada?.id) throw new Error("Perfil selecionado invalido.");
    await excluirPerfilTrilhoEstaleiro(rowSelecionada.id);
    setFeedback("Perfil excluido com sucesso.");
    reload();
  }

  async function ativarPerfil() {
    if (!rowSelecionada?.id) throw new Error("Perfil selecionado invalido.");
    await alterarStatusPerfilTrilhoEstaleiro(rowSelecionada.id, 1);
    setFeedback("Perfil ativado com sucesso.");
    reload();
  }

  async function inativarPerfil() {
    if (!rowSelecionada?.id) throw new Error("Perfil selecionado invalido.");
    await alterarStatusPerfilTrilhoEstaleiro(rowSelecionada.id, 0);
    setFeedback("Perfil inativado com sucesso.");
    reload();
  }

  return (
    <div className="bep-base">
      <div className="bep-cards">
        <div className="bep-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="bep-card">
          <span>Ativos</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="bep-card">
          <span>Inativos</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <div className="bep-toolbar">
        <input
          type="text"
          value={filtroTexto}
          onChange={(event) => setFiltroTexto(event.target.value)}
          placeholder="Buscar perfil de trilho..."
        />
        <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
          <option value="todos">Todos</option>
          <option value="ativos">Somente ativos</option>
          <option value="inativos">Somente inativos</option>
        </select>
        <button
          type="button"
          className="bep-btn bep-btn--primary"
          onClick={() => {
            setFeedback("");
            setOpenCriar(true);
          }}
        >
          Cadastrar perfil
        </button>
      </div>

      <div className="bep-table-wrap">
        <table className="bep-table bep-table--head">
          <colgroup>
            <col style={{ width: "65%" }} />
            <col style={{ width: "35%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Perfil de trilho</th>
              <th>Controles</th>
            </tr>
          </thead>
        </table>

        <div className="bep-table-scroll" ref={tableScrollRef} onScroll={handleTableScroll}>
          <table className="bep-table bep-table--body">
            <colgroup>
              <col style={{ width: "65%" }} />
              <col style={{ width: "35%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={2} className="bep-empty">Carregando base de perfis...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={2} className="bep-empty bep-empty--error">{error}</td>
                </tr>
              ) : !rowsOrdenadas.length ? (
                <tr>
                  <td colSpan={2} className="bep-empty">Nenhum perfil encontrado.</td>
                </tr>
              ) : (
                rowsOrdenadas.map((row) => {
                  const ativo = Number(row?.ativo) === 1;
                  return (
                    <tr key={row.id}>
                      <td>{safeText(row?.perfil_trilho)}</td>
                      <td>
                        <div className="bep-actions">
                          <ToggleSwitch
                            on={ativo}
                            title={ativo ? "Inativar perfil" : "Ativar perfil"}
                            onToggle={() => {
                              setRowSelecionada(row);
                              if (ativo) setOpenInativar(true);
                              else setOpenAtivar(true);
                            }}
                          />
                          <button
                            type="button"
                            className="bep-icon-btn"
                            title="Editar perfil"
                            onClick={() => {
                              setFeedback("");
                              setRowSelecionada(row);
                              setOpenEditar(true);
                            }}
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="bep-icon-btn"
                            title="Excluir perfil"
                            onClick={() => {
                              setRowSelecionada(row);
                              setOpenExcluir(true);
                            }}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {!loading && !error && loadingMore ? (
                <tr>
                  <td colSpan={2} className="bep-empty">Carregando mais...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {feedback ? <p className="bep-feedback">{feedback}</p> : null}

      <CriarPerfilTrilhoModal
        open={openCriar}
        onClose={() => setOpenCriar(false)}
        onSalvar={criarPerfil}
      />

      <EditarPerfilTrilhoModal
        open={openEditar}
        numeroInicial={numeroFromPerfil(rowSelecionada?.perfil_trilho)}
        onClose={() => {
          setOpenEditar(false);
          setRowSelecionada(null);
        }}
        onSalvar={editarPerfil}
      />

      <ExcluirPerfilTrilhoModal
        open={openExcluir}
        titulo={safeText(rowSelecionada?.perfil_trilho)}
        onClose={() => {
          setOpenExcluir(false);
          setRowSelecionada(null);
        }}
        onConfirmar={excluirPerfil}
      />

      <AtivarPerfilTrilhoModal
        open={openAtivar}
        titulo={safeText(rowSelecionada?.perfil_trilho)}
        onClose={() => {
          setOpenAtivar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={ativarPerfil}
      />

      <InativarPerfilTrilhoModal
        open={openInativar}
        titulo={safeText(rowSelecionada?.perfil_trilho)}
        onClose={() => {
          setOpenInativar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={inativarPerfil}
      />
    </div>
  );
}
